/**
 * Réglages métier : lecture et enregistrement.
 *
 * Même mécanique que les marqueurs : une requête sans paramètre, partagée par
 * toute l'application, et une écriture qui invalide la clé plutôt que de
 * deviner ce que l'API a enregistré (elle arrondit le taux au dixième).
 */

import type { BusinessSettings, BusinessSettingsInput } from '@shared/types/settings';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, fetchBusinessSettings, saveBusinessSettings } from '../lib/api';
import { useAuth } from './useAuth';

const SETTINGS_KEY = ['business-settings'] as const;

export function useBusinessSettings() {
  const { token, signOut } = useAuth();
  return useQuery<BusinessSettings, Error>({
    queryKey: SETTINGS_KEY,
    queryFn: async ({ signal }) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      try {
        return (await fetchBusinessSettings(token, signal)).settings;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) signOut();
        throw error;
      }
    },
    enabled: Boolean(token),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) =>
      !(error instanceof ApiRequestError && error.status >= 400 && error.status < 500) && failureCount < 2,
  });
}

export function useSaveBusinessSettings() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<BusinessSettings, Error, BusinessSettingsInput>({
    mutationFn: async (input) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      return (await saveBusinessSettings(input, token)).settings;
    },
    // Tout est relu, pas seulement les réglages : le coût de production d'un
    // kit entre dans le COGS calculé par l'API (ventes, marges, tableaux).
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
}
